import { usePrepareCart } from '@cannect/hooks/usePrepareCart'

import { formatReal } from '@cannect/utils/formatters'
import {
  FREE_NATIONAL_FREIGHT_LABEL,
  hasQuotedNationalFreight,
  shouldShowNationalFreight
} from '@cannect/utils/nationalFreight'
import { getPixDiscount } from '@cannect/utils/pixDiscount'
import { getPayableAmount } from '@cannect/utils/reshipping'

export const Sumary = ({
  itens,
  importCost = 145.0,
  selectedPaymentMethod
}: {
  itens: any[]
  importCost: number
  selectedPaymentMethod: string
}) => {
  const { lastOrderCreated } = usePrepareCart()

  // Se não há itens, retornar resumo vazio
  if (!itens || itens.length === 0) {
    return (
      <div className="space-y-7">
        <h3 className="text-lg font-semibold text-gray-900">Resumo do Pedido</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Produtos (0)</span>
            <span>R$ 0,00</span>
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 pt-3 text-lg font-semibold text-gray-900">
            <span className="text-base font-normal text-gray-800">Total</span>
            <span>R$ 0,00</span>
          </div>
        </div>
      </div>
    )
  }

  const productsTotal = itens.reduce(
    (acc: number, item: any) => acc + parseFloat(item.unitPrice) * (item.quantity || 0),
    0
  )

  const noImportCost =
    lastOrderCreated?.noImportCost ?? lastOrderCreated?.summary?.no_import_cost ?? false
  const nationalFreight =
    parseFloat(
      lastOrderCreated?.national_freight ?? lastOrderCreated?.summary?.national_freight ?? 0
    ) || 0

  const productDiscounts = itens.reduce((acc: number, item: any) => {
    const itemDiscount = parseFloat(item.discount || '0')

    if (itemDiscount === 0 && item.product?.price && item.product?.real_price) {
      const originalPrice = item.product.price * item.quantity
      const finalPrice = item.product.real_price * item.quantity
      const calculatedDiscount = Math.max(0, originalPrice - finalPrice)
      return acc + calculatedDiscount
    }

    return acc + itemDiscount
  }, 0)

  const subtotal = productsTotal - productDiscounts
  const total = subtotal + importCost + nationalFreight

  const freeShippingThreshold = 2300
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - total)

  const importDiscount = remainingForFreeShipping <= 0 ? importCost : 0

  const shipping = lastOrderCreated?.shipping || importCost
  const shippingDiscount = lastOrderCreated?.shippingDiscount || 0

  const finalTotal = total - importDiscount

  const amount = getPayableAmount(lastOrderCreated) || finalTotal

  const { applied: applyPixDiscount, percentage: pixDiscountPercentage } = getPixDiscount(lastOrderCreated)
  const isPixPayment = selectedPaymentMethod === 'pix'

  const applyPaymentDiscount = (amount: number) => {
    if (applyPixDiscount && isPixPayment) {
      const discount = amount * (pixDiscountPercentage / 100)
      return amount - discount
    }
    return amount
  }

  const pixDiscountAmount = isPixPayment && applyPixDiscount ? amount * (pixDiscountPercentage / 100) : 0
  const finalAmountWithPixDiscount = isPixPayment && applyPixDiscount ? applyPaymentDiscount(amount) : amount

  return (
    <div className="space-y-7">
      <h3 className="text-lg font-semibold text-gray-900">Resumo do Pedido</h3>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Produtos ({itens.length})</span>
          <span>R$ {productsTotal.toFixed(2).replace('.', ',')}</span>
        </div>
        {productDiscounts > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Descontos nos produtos</span>
            <span>- R$ {productDiscounts.toFixed(2).replace('.', ',')}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Subtotal</span>
          <span>R$ {subtotal.toFixed(2).replace('.', ',')}</span>
        </div>

        {!noImportCost && (
          <>
            <div className="pt-4">
              <hr className="h-[0.2px] border-none bg-gray-200" />
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Custo de importação</span>
              <span>{formatReal(shipping)}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Descontos na importação</span>
              <span>{shippingDiscount > 0 ? `- ${formatReal(shippingDiscount)}` : '- R$ 0,00'}</span>
            </div>
          </>
        )}

        {shouldShowNationalFreight(nationalFreight, hasQuotedNationalFreight(lastOrderCreated)) && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Frete nacional</span>
            <span>{nationalFreight > 0 ? formatReal(nationalFreight) : FREE_NATIONAL_FREIGHT_LABEL}</span>
          </div>
        )}

        {isPixPayment && applyPixDiscount && pixDiscountAmount > 0 && (
          <div className="flex items-center justify-between text-sm text-green-600">
            <span>Desconto PIX (-{pixDiscountPercentage}%)</span>
            <span>- {formatReal(pixDiscountAmount)}</span>
          </div>
        )}
        <div className="pt-4">
          <hr className="h-[0.2px] border-none bg-gray-200" />
        </div>
        <div className="flex items-center justify-between border-t border-gray-200 pt-3 text-lg font-semibold text-gray-900">
          <span className="text-base font-normal text-gray-800">Total</span>
          <div className="flex flex-col items-end">
            {isPixPayment && applyPixDiscount && pixDiscountAmount > 0 && (
              <span className="text-sm text-gray-500 line-through">{formatReal(amount)}</span>
            )}
            <span className={isPixPayment && applyPixDiscount && pixDiscountAmount > 0 ? 'text-green-600' : ''}>
              {formatReal(finalAmountWithPixDiscount)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
